import { ANIMATION_CONFIG } from './const';

export function calculateProcessingDataStatic(
  time: number,
  barCount: number,
  transitionProgress: number,
  lastActiveData: number[],
): number[] {
  const processingData: number[] = [];
  const halfCount = Math.floor(barCount / 2);

  for (let i = 0; i < barCount; i++) {
    const normalizedPosition = (i - halfCount) / halfCount;
    const centerWeight = 1 - Math.abs(normalizedPosition) * 0.4;
    const wave1 = Math.sin(time * 1.5 + normalizedPosition * 3) * 0.25;
    const wave2 = Math.sin(time * 0.8 - normalizedPosition * 2) * 0.2;
    const wave3 = Math.cos(time * 2 + normalizedPosition) * 0.15;
    const combinedWave = wave1 + wave2 + wave3;
    const processingValue = (0.2 + combinedWave) * centerWeight;

    let finalValue = processingValue;
    if (lastActiveData.length > 0 && transitionProgress < 1) {
      const lastDataIndex = Math.min(i, lastActiveData.length - 1);
      const lastValue = lastActiveData[lastDataIndex] || 0;
      finalValue = lastValue * (1 - transitionProgress) + processingValue * transitionProgress;
    }

    processingData.push(Math.max(ANIMATION_CONFIG.minValue, Math.min(1, finalValue)));
  }

  return processingData;
}

export function calculateProcessingDataScrolling(
  time: number,
  barCount: number,
  transitionProgress: number,
  lastActiveData: number[],
): number[] {
  const processingData: number[] = [];

  for (let i = 0; i < barCount; i++) {
    const normalizedPosition = (i - barCount / 2) / (barCount / 2);
    const centerWeight = 1 - Math.abs(normalizedPosition) * 0.4;
    const wave1 = Math.sin(time * 1.5 + i * 0.15) * 0.25;
    const wave2 = Math.sin(time * 0.8 - i * 0.1) * 0.2;
    const wave3 = Math.cos(time * 2 + i * 0.05) * 0.15;
    const combinedWave = wave1 + wave2 + wave3;
    const processingValue = (0.2 + combinedWave) * centerWeight;

    let finalValue = processingValue;
    if (lastActiveData.length > 0 && transitionProgress < 1) {
      const lastDataIndex = Math.floor((i / barCount) * lastActiveData.length);
      const lastValue = lastActiveData[lastDataIndex] || 0;
      finalValue = lastValue * (1 - transitionProgress) + processingValue * transitionProgress;
    }

    processingData.push(Math.max(ANIMATION_CONFIG.minValue, Math.min(1, finalValue)));
  }

  return processingData;
}

export function calculateStaticBarsFromFrequency(
  dataArray: Uint8Array,
  barCount: number,
  sensitivity: number,
): number[] {
  const startFreq = Math.floor(dataArray.length * ANIMATION_CONFIG.freqStart);
  const endFreq = Math.floor(dataArray.length * ANIMATION_CONFIG.freqEnd);
  const relevantData = dataArray.slice(startFreq, endFreq);
  const halfCount = Math.floor(barCount / 2);
  const newBars: number[] = [];

  for (let i = halfCount - 1; i >= 0; i--) {
    const dataIndex = Math.floor((i / halfCount) * relevantData.length);
    const value = Math.min(1, (relevantData[dataIndex] / 255) * sensitivity);
    newBars.push(Math.max(ANIMATION_CONFIG.minValue, value));
  }

  for (let i = 0; i < halfCount; i++) {
    const dataIndex = Math.floor((i / halfCount) * relevantData.length);
    const value = Math.min(1, (relevantData[dataIndex] / 255) * sensitivity);
    newBars.push(Math.max(ANIMATION_CONFIG.minValue, value));
  }

  return newBars;
}

export function calculateScrollingValueFromFrequency(dataArray: Uint8Array, sensitivity: number): number {
  const startFreq = Math.floor(dataArray.length * ANIMATION_CONFIG.freqStart);
  const endFreq = Math.floor(dataArray.length * ANIMATION_CONFIG.freqEnd);
  const relevantData = dataArray.slice(startFreq, endFreq);

  let sum = 0;
  for (let i = 0; i < relevantData.length; i++) {
    sum += relevantData[i];
  }

  const average = (sum / relevantData.length / 255) * sensitivity;
  return Math.min(1, Math.max(ANIMATION_CONFIG.minValue, average));
}

export function createFadeGradient(
  ctx: CanvasRenderingContext2D,
  width: number,
  fadeWidth: number,
): CanvasGradient {
  const gradient = ctx.createLinearGradient(0, 0, width, 0);
  const fadePercent = Math.min(0.3, fadeWidth / width);

  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(fadePercent, 'rgba(255,255,255,0)');
  gradient.addColorStop(1 - fadePercent, 'rgba(255,255,255,0)');
  gradient.addColorStop(1, 'rgba(255,255,255,1)');

  return gradient;
}

export function drawRoundedBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  color: string,
  alpha: number,
): void {
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;

  if (radius > 0) {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, width, height);
  }
}
